<template>
  <v-dialog
    v-model="localShow"
    max-width="500px"
    persistent
    @click:outside="handleCancel"
    @keydown.esc="handleCancel"
  >
    <v-card class="confirm-delete-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="dialog-header">
        <div class="d-flex align-center">
          <v-icon
            icon="mdi-alert-circle"
            size="28"
            color="error"
            class="mr-3"
          ></v-icon>
          <div>
            <h2 class="dialog-title">
              Eliminación no disponible
            </h2>
            <p class="dialog-subtitle mb-0">
              La incidencia y su historial se conservan
            </p>
          </div>
        </div>
      </v-card-title>

      <!-- Contenido del diálogo -->
      <v-card-text class="pa-6">
        <!-- Advertencia principal -->
        <v-alert
          type="error"
          variant="tonal"
          class="mb-4"
          prominent
        >
          <template v-slot:prepend>
            <v-icon size="24">mdi-delete-alert</v-icon>
          </template>
          <div class="alert-content">
            <div class="alert-title">
              <strong>La eliminación no está disponible</strong>
            </div>
            <div class="alert-message">
              Una incidencia conserva su historial de estados, sus comentarios y sus dependencias
              operativas, así que no puede eliminarse desde esta consola. Si continúas, el servidor
              rechaza la operación y todo permanece intacto.
            </div>
          </div>
        </v-alert>

        <!-- Información de la incidencia (modo simple) -->
        <div v-if="!multiple && incident" class="incident-details mb-4">
          <v-card variant="outlined" class="incident-summary">
            <v-card-text class="pa-4">
              <div class="incident-header">
                <div class="incident-id">
                  <strong>Incidencia #{{ incident.id }}</strong>
                </div>
                <div class="incident-status">
                  <v-chip
                    :color="getStatusColor(incident.status)"
                    size="small"
                    variant="tonal"
                  >
                    {{ getStatusText(incident.status) }}
                  </v-chip>
                </div>
              </div>
              
              <div class="incident-title">
                {{ incident.title }}
              </div>
              
              <div class="incident-meta">
                <div class="meta-item">
                  <v-icon icon="mdi-flag" size="16" class="mr-1"></v-icon>
                  Prioridad: 
                  <v-chip
                    :color="getPriorityColor(incident.priority)"
                    size="x-small"
                    variant="tonal"
                    class="ml-1"
                  >
                    {{ getPriorityText(incident.priority) }}
                  </v-chip>
                </div>
                
                <div class="meta-item">
                  <v-icon icon="mdi-tag" size="16" class="mr-1"></v-icon>
                  Categoría: {{ getCategoryText(incident.category) }}
                </div>
                
                <div class="meta-item">
                  <v-icon icon="mdi-calendar" size="16" class="mr-1"></v-icon>
                  Creada: {{ formatDate(incident.createdAt) }}
                </div>
                
                <div v-if="incident.assignedTo" class="meta-item">
                  <v-icon icon="mdi-account" size="16" class="mr-1"></v-icon>
                  Asignada a: {{ incident.assignedTo }}
                </div>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Lista de incidencias (modo múltiple) -->
        <div v-if="multiple" class="multiple-incidents mb-4">
          <div class="selection-summary">
            <h3 class="mb-3">
              <v-icon icon="mdi-format-list-bulleted" class="mr-2"></v-icon>
              Incidencias seleccionadas ({{ selectedCount }})
            </h3>
          </div>
          
          <v-card variant="outlined" class="incidents-list">
            <v-list density="compact">
              <v-list-item
                v-for="(incident, index) in selectedIncidents"
                :key="incident.id"
                class="incident-list-item"
              >
                <template v-slot:prepend>
                  <v-icon
                    :icon="getPriorityIcon(incident.priority)"
                    :color="getPriorityColor(incident.priority)"
                    size="20"
                  ></v-icon>
                </template>
                
                <v-list-item-title class="incident-item-title">
                  #{{ incident.id }} - {{ incident.title }}
                </v-list-item-title>
                
                <v-list-item-subtitle class="incident-item-subtitle">
                  {{ getStatusText(incident.status) }} • {{ getCategoryText(incident.category) }}
                </v-list-item-subtitle>
                
                <template v-slot:append>
                  <v-chip
                    :color="getPriorityColor(incident.priority)"
                    size="x-small"
                    variant="tonal"
                  >
                    {{ getPriorityText(incident.priority) }}
                  </v-chip>
                </template>
              </v-list-item>
            </v-list>
          </v-card>
        </div>

        <!-- Consecuencias de la eliminación -->
        <v-card flat class="consequences-card mb-4">
          <v-card-title class="text-h6 pb-2">
            <v-icon icon="mdi-information" class="mr-2"></v-icon>
            Qué se conserva:
          </v-card-title>
          <v-card-text class="pt-0">
            <v-list density="compact">
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-file-document" color="success" size="20"></v-icon>
                </template>
                <v-list-item-title>Información de la incidencia</v-list-item-title>
                <v-list-item-subtitle>Título, descripción, prioridad, estado</v-list-item-subtitle>
              </v-list-item>
              
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-comment-multiple" color="success" size="20"></v-icon>
                </template>
                <v-list-item-title>Comentarios y seguimiento</v-list-item-title>
                <v-list-item-subtitle>Historial de comentarios y cambios</v-list-item-subtitle>
              </v-list-item>
              
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-history" color="success" size="20"></v-icon>
                </template>
                <v-list-item-title>Historial de estados</v-list-item-title>
                <v-list-item-subtitle>Registro de cambios de estado</v-list-item-subtitle>
              </v-list-item>
              
              <v-list-item>
                <template v-slot:prepend>
                  <v-icon icon="mdi-paperclip" color="success" size="20"></v-icon>
                </template>
                <v-list-item-title>Archivos adjuntos</v-list-item-title>
                <v-list-item-subtitle>Imágenes y documentos relacionados</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>

        <!-- P13-V1-V6B: aquí se pedía escribir "ELIMINAR" para confirmar. Esa ceremonia existe para
             que nadie destruya algo por accidente, y aquí no había nada que destruir: era teatro que
             además hacía más creíble la promesa falsa. La política la decide el servidor, no este
             diálogo, así que la acción sigue existiendo y sigue preguntando — sin ritual. -->
      </v-card-text>

      <!-- Acciones del diálogo -->
      <v-card-actions class="dialog-actions pa-6 pt-0">
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          @click="handleCancel"
          :disabled="busy"
        >
          Cerrar
        </v-btn>
        <v-btn
          color="primary"
          variant="elevated"
          @click="handleConfirm"
          :loading="busy"
          :disabled="busy"
          prepend-icon="mdi-shield-alert-outline"
        >
          {{ multiple ? 'Solicitar eliminación de todas' : 'Solicitar eliminación' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch } from 'vue'
import type { Incident } from '../../../types/incidents'

interface Props {
  modelValue: boolean
  incident?: Incident | null
  multiple?: boolean
  selectedIncidents?: Incident[]
  /**
   * Estado de la petición, propiedad del PADRE: `true` mientras la solicitud está en vuelo, `false`
   * cuando termina — por éxito, por política o por fallo. El diálogo no puede deducirlo solo.
   */
  requestPending?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  selectedIncidents: () => [],
  requestPending: false
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  confirm: []
  cancel: []
}>()

// Cerrojo local que cubre EXACTAMENTE el hueco de un tick entre el `emit` —síncrono— y la llegada
// del prop del padre —que necesita un render—. Sin él, dos clics en el mismo tick salen los dos.
const localPending = ref(false)

/** La verdad de «ocupado»: el padre manda; el cerrojo sólo tapa el tick que el padre no puede. */
const busy = computed(() => props.requestPending || localPending.value)

// Computed
const localShow = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const selectedCount = computed(() => {
  return props.multiple ? props.selectedIncidents.length : 1
})

// Métodos
// P13-V1-V6B: aquí había una espera artificial de 1,5 s que simulaba progreso antes de que existiera
// petición alguna. El diálogo ya no finge trabajo: delega en el padre, que hace la petición real.
//
// Y tampoco inventa el final de esa petición. Antes marcaba un `deleting` propio que sólo se
// liberaba al REABRIR el diálogo; como la denegación canónica de política (409) conserva el diálogo
// ABIERTO, ese momento no llegaba nunca: el spinner se quedaba encendido y «Cerrar» deshabilitado
// sobre un mensaje que ya lo explicaba todo. El fin de la petición lo conoce el padre, y llega por
// `requestPending`; el cerrojo local sólo cubre el tick en que ese prop aún no ha viajado.
const handleConfirm = () => {
  if (busy.value) return
  localPending.value = true
  emit('confirm')
  // Tras este tick el padre ya reflejó REQUEST_PENDING y manda él: si la petición sigue en vuelo,
  // `requestPending` mantiene el estado; si ya terminó, el control vuelve al usuario de inmediato.
  nextTick(() => { localPending.value = false })
}

const handleCancel = () => {
  // No se cierra con una petición en vuelo: sería descartar el sujeto de algo que aún puede responder.
  if (busy.value) return
  localShow.value = false
  emit('cancel')
}

watch(() => props.modelValue, (open) => {
  if (open) localPending.value = false
})

// Métodos auxiliares para formateo
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, string> = {
    CRITICAL: 'mdi-alert-circle',
    HIGH: 'mdi-alert',
    MEDIUM: 'mdi-information',
    LOW: 'mdi-check-circle'
  }
  return icons[priority] || 'mdi-help'
}

const getPriorityText = (priority: string) => {
  const texts: Record<string, string> = {
    CRITICAL: 'Crítica',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Baja'
  }
  return texts[priority] || priority
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    RESOLVED: 'success',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    RESOLVED: 'Resuelto',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }
  return texts[status] || status
}

const getCategoryText = (category: string) => {
  const texts: Record<string, string> = {
    PERSONAL: 'Personal',
    TECHNICAL: 'Técnica',
    INFRASTRUCTURE: 'Infraestructura',
    ELECTRICAL: 'Eléctrica',
    INTERNET: 'Internet',
    EQUIPMENT: 'Equipos',
    OTHER: 'Otra'
  }
  return texts[category] || category
}

</script>

<style scoped>
.confirm-delete-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
  border-bottom: 1px solid #f48fb1;
  padding: 1.5rem 2rem;
}

.dialog-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #c62828;
  margin: 0;
}

.dialog-subtitle {
  color: #d32f2f;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.dialog-actions {
  background: #fafafa;
  border-top: 1px solid #dee2e6;
}

/* Alert de advertencia */
.alert-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.alert-title {
  font-size: 1.1rem;
}

.alert-message {
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Detalles de la incidencia */
.incident-summary {
  border: 2px solid #ffcdd2 !important;
}

.incident-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.incident-id {
  font-size: 1rem;
  color: #d32f2f;
}

.incident-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 1rem;
}

.incident-meta {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meta-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #666;
}

/* Lista múltiple */
.selection-summary h3 {
  color: #d32f2f;
  font-size: 1.1rem;
}

.incidents-list {
  max-height: 300px;
  overflow-y: auto;
  border: 2px solid #ffcdd2 !important;
}

.incident-list-item {
  border-bottom: 1px solid #f5f5f5;
}

.incident-list-item:last-child {
  border-bottom: none;
}

.incident-item-title {
  font-weight: 500 !important;
  font-size: 0.95rem !important;
}

.incident-item-subtitle {
  font-size: 0.8rem !important;
}

/* Consecuencias */
.consequences-card {
  background: #fff3e0 !important;
  border: 1px solid #ffcc02 !important;
}

.consequences-card .v-card-title {
  color: #e65100;
}

/* Mejoras en los campos del formulario */
:deep(.v-field--variant-outlined) {
  border-radius: 8px;
}

:deep(.v-text-field--error .v-field__outline) {
  border-color: #dc3545 !important;
  border-width: 2px !important;
}

/* Responsive */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-title {
    font-size: 1.2rem;
  }
  
  :deep(.v-card-text) {
    padding: 1rem !important;
  }
  
  .dialog-actions {
    padding: 1rem !important;
    padding-top: 0 !important;
  }
  
  .incident-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .meta-item {
    flex-wrap: wrap;
  }
}

/* Animaciones */
.confirm-delete-dialog {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* Estados de peligro */
.v-btn--variant-elevated.bg-error {
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3) !important;
}

.v-btn--variant-elevated.bg-error:hover {
  box-shadow: 0 6px 16px rgba(244, 67, 54, 0.4) !important;
}

/* Scrollbar personalizado para la lista */
.incidents-list::-webkit-scrollbar {
  width: 6px;
}

.incidents-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.incidents-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.incidents-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mejoras de accesibilidad */
:deep(.v-btn:focus-visible) {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

:deep(.v-field:focus-within) {
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.25);
}
</style>
